import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import { change } from 'redux-form'
import { DayPickerRangeController } from 'react-dates'
// @ts-ignore -- no typings for react-dates/constants
import { START_DATE, END_DATE } from 'react-dates/constants'
import moment, { Moment } from 'moment'
import styled from 'styled-components'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'
import DialogModal from 'components/display/DialogModal'
import { HeadingMd } from 'components/display/Typography'
import InvisibleButton from 'components/display/InvisibleButton'
import './overrides'
import AvailabilityInfo from './AvailabilityInfo'
import CalendarDay from './CalendarDay'
import {
  findAvailability,
  buildFindNextUnavailableDay,
  buildIsDayBlocked,
  buildIsOutsideRange,
} from './dayAvailability'
import useFirstUnavailableDate from './useFirstUnavailableDate'
import DatesInput from './DatesInput'
import { CalendarWrapper } from './styles'
import { FocusedInput, DaysAvailabilities, TooltipConfig } from './types'
import useDiscountsVisible from './useDiscountsVisible'

const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  margin-left: 20px;
  width: calc(100% - 32px);
  border-bottom: 1px solid ${({ theme }) => theme?.['-ds-neutral-300']?.value};
  padding-bottom: 16px;
  margin-bottom: 8px;

  ${InvisibleButton} {
    color: ${({ theme }) => theme?.['-ds-core-400']?.value};
    text-decoration: underline;
    text-underline-offset: 2px;
    font-weight: 400;
    margin-left: 10px;
  }
`

export interface BookingFormCalendarProps {
  start_date?: string
  end_date?: string
  dispatch: (arg0: any) => void
  formName: string
  daysData: DaysAvailabilities
  listing: {
    concurrentBookings: boolean
    listingType: {
      slug: string
    }
  }
  maxStartDate: string
  onMonthsForward?: (arg0: Moment) => any
  onMonthsBack?: (arg0: Moment) => any
  onCalendarClose?: () => any
  snoozed?: boolean
}

const BookingFormCalendar = (props: BookingFormCalendarProps) => {
  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    start_date,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    end_date,
    daysData,
    dispatch,
    formName,
    listing,
    maxStartDate,
    onMonthsForward,
    onMonthsBack,
    onCalendarClose,
    snoozed,
  } = props

  const { lg: largerThanLg } = useMediaQueryContext()
  const [startDate, setStartDate] = useState<Moment>(null)
  const [endDate, setEndDate] = useState<Moment>(null)
  const [focusedInput, setFocusedInput] = useState<FocusedInput>(START_DATE)
  const [minimumNights, setMinimumNights] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [isDayPickerFocused, setIsDayPickerFocused] = useState(true)

  useEffect(() => {
    if (start_date) setStartDate(moment(start_date))
    if (end_date) setEndDate(moment(end_date))
  }, [start_date, end_date])

  const onDatesChange = ({
    startDate: newStartDate,
    endDate: newEndDate,
  }: {
    startDate: Moment
    endDate: Moment
  }): void => {
    /**
     * `DayPickerRangeController` caches the currently focused date for keyboard
     * accessibility, this means when the user changes the startDate the focus
     * does not switch to the new date. To work around this we need the component
     * to see the `isFocused` prop change by toggle to false here, then back to
     * true in onBlur. To avoid React batching hiding the toggle behaviour we
     * need to use flushSync
     */
    ReactDOM.flushSync(() => {
      setIsDayPickerFocused(false)
    })

    const availability = findAvailability({ daysData, day: newStartDate })

    if (availability?.minimumNights) {
      setMinimumNights(availability.minimumNights)
    }

    if (!startDate || newStartDate.isSame(startDate)) {
      dispatch(
        change(
          formName,
          'booking[start_date]',
          newStartDate && newStartDate.format('YYYY-MM-DD')
        )
      )
      dispatch(
        change(
          formName,
          'booking[end_date]',
          newEndDate && newEndDate.format('YYYY-MM-DD')
        )
      )

      setStartDate(newStartDate)
      setEndDate(newEndDate)
    } else {
      /**
       * QA: If a user already has a date range selected,
       * when they go to select a new date range,
       * clear the existing selection after the first date is selected
       */
      dispatch(
        change(
          formName,
          'booking[start_date]',
          newStartDate && newStartDate.format('YYYY-MM-DD')
        )
      )
      dispatch(change(formName, 'booking[end_date]', null))
      setStartDate(newStartDate)
      setEndDate(null)
    }

    if (newEndDate && !newEndDate.isSame(endDate)) {
      setModalOpen(false)
    }
  }

  const handleDateClick = useCallback(
    (type: 'start' | 'end') => {
      if (type === 'start') {
        setFocusedInput(START_DATE)
      } else {
        setFocusedInput(startDate ? END_DATE : START_DATE)
      }

      setModalOpen(true)
    },
    [startDate]
  )

  const handleClearDates = useCallback(() => {
    setStartDate(null)
    setEndDate(null)
    setFocusedInput(START_DATE)
  }, [])

  const maxStart = useMemo(
    () => maxStartDate && moment(maxStartDate).endOf('day'),
    [maxStartDate]
  )

  const isDayBlocked = useMemo(
    () => buildIsDayBlocked({ focusedInput, startDate, daysData }),
    [focusedInput, startDate, daysData]
  )

  const findNextUnavailableDay = useMemo(
    () => buildFindNextUnavailableDay({ daysData }),
    [daysData]
  )

  const today = useMemo(() => moment().startOf('day'), [new Date().getDay()])
  const firstUnavailableDate = useFirstUnavailableDate(
    startDate,
    findNextUnavailableDay
  )

  const isOutsideRange = useMemo(
    () =>
      buildIsOutsideRange({
        today,
        focusedInput,
        startDate,
        maxStart,
        daysData,
        firstUnavailableDate,
        findNextUnavailableDay,
      }),
    [
      today,
      focusedInput,
      startDate,
      maxStart,
      daysData,
      firstUnavailableDate,
      findNextUnavailableDay,
    ]
  )

  const [tooltip, setTooltip] = useState<TooltipConfig>(null)

  const numberOfMonths = largerThanLg ? 2 : 1
  const { discountsVisible, updateCurrentMonth } = useDiscountsVisible(
    startDate,
    numberOfMonths,
    daysData
  )

  const startDayData =
    startDate && findAvailability({ day: startDate, daysData })

  const renderAvailabilityInfo = () => (
    <AvailabilityInfo
      discountsVisible={discountsVisible}
      activeDiscount={startDayData?.discount}
      minimumNights={startDayData?.minimumNights}
      concurrentBookings={listing.concurrentBookings}
      snoozed={snoozed}
      tooltip={tooltip}
      onCloseTooltip={(closedTooltip) => {
        setTooltip((lastTooltip) => {
          if (
            closedTooltip.referenceElement === lastTooltip?.referenceElement
          ) {
            return null
          }

          return lastTooltip
        })
      }}
    />
  )

  const renderDayContents = useCallback(
    (day: Moment) => (
      <CalendarDay
        focusedInput={focusedInput}
        day={day}
        daysData={daysData}
        today={today}
        maxStartDate={maxStart}
        startDate={startDate}
        firstUnavailableDate={firstUnavailableDate}
        activeDiscount={startDayData?.discount}
        onTooltipOpen={(referenceElement, message) => {
          setTooltip({ referenceElement, message })
        }}
      />
    ),
    [
      focusedInput,
      daysData,
      today,
      maxStart,
      startDate,
      startDayData,
      firstUnavailableDate,
    ]
  )

  const handleNextMonthClick = useCallback(
    (currentMonth) => {
      updateCurrentMonth(currentMonth)
      return onMonthsForward ? onMonthsForward(currentMonth) : undefined
    },
    [onMonthsForward, updateCurrentMonth]
  )

  const handlePrevMonthClick = useCallback(
    (currentMonth) => {
      updateCurrentMonth(currentMonth)
      return onMonthsBack ? onMonthsBack(currentMonth) : undefined
    },
    [onMonthsBack, updateCurrentMonth]
  )

  return (
    <>
      <DatesInput
        onDateClick={handleDateClick}
        startDate={startDate}
        endDate={endDate}
      />

      <DialogModal
        isOpen={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        smallModal
        autoWidth
        closeButtonColour="-ds-neutral-700"
        rounded
        noPadding
      >
        <CalendarWrapper>
          <CalendarHeader>
            <HeadingMd as="h3" weight="500" noMargin>
              {!startDate && !endDate && 'Select dates'}
              {startDate && `${moment(startDate).format('DD MMM')} - `}
              {endDate && moment(endDate).format('DD MMM')}
            </HeadingMd>

            {startDate && (
              <InvisibleButton
                type="button"
                aria-label="Clear dates"
                onClick={handleClearDates}
              >
                Clear dates
              </InvisibleButton>
            )}
          </CalendarHeader>

          <DayPickerRangeController
            startDate={startDate}
            endDate={endDate}
            isFocused={isDayPickerFocused}
            focusedInput={focusedInput as 'startDate' | 'endDate'}
            minimumNights={minimumNights}
            onDatesChange={onDatesChange}
            isDayBlocked={isDayBlocked}
            isOutsideRange={isOutsideRange}
            minDate={moment()}
            onFocusChange={setFocusedInput}
            onNextMonthClick={handleNextMonthClick}
            onPrevMonthClick={handlePrevMonthClick}
            onClose={onCalendarClose}
            numberOfMonths={numberOfMonths}
            renderCalendarInfo={renderAvailabilityInfo}
            daySize={largerThanLg ? 56 : 48}
            renderDayContents={renderDayContents}
            initialVisibleMonth={null}
            noBorder
            onBlur={() => setIsDayPickerFocused(true)}
            hideKeyboardShortcutsPanel
            calendarInfoPosition="bottom"
            firstDayOfWeek={1}
            transitionDuration={0} // Fix for iOS 14
          />
        </CalendarWrapper>
      </DialogModal>
    </>
  )
}

export default BookingFormCalendar
